import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { VersionPreviewRecord } from '@/services/version-preview';

/**
 * 独立版本 Preview 页面状态 Store。
 *
 * 说明：该 Store 只保存页面 UI 状态，不直接操作 Viewer；只读语义仍同步给
 * version-workspace store，使设计树、右键菜单和编辑入口复用同一套只读判断。
 */
export const useVersionPreviewStore = defineStore('version-preview', () => {
  const records = ref<VersionPreviewRecord[]>([]);
  const selectedVersionId = ref<string | null>(null);
  const switchingVersionId = ref<string | null>(null);
  const isRecordsLoading = ref(false);
  const isVersionSwitching = ref(false);
  const recordsError = ref<string | null>(null);
  const switchError = ref<string | null>(null);

  /**
   * 当前选中的版本记录。
   */
  const selectedRecord = computed(() => {
    if (!selectedVersionId.value) {
      return undefined;
    }

    return records.value.find((record) => record.id === selectedVersionId.value);
  });

  /**
   * 当前采纳版本记录。
   */
  const adoptedRecord = computed(() => records.value.find((record) => record.isAdoptedVersion));

  /**
   * 写入版本列表。
   *
   * @param nextRecords 最新版本记录列表。
   */
  function setRecords(nextRecords: readonly VersionPreviewRecord[]): void {
    records.value = nextRecords.map((record) => ({ ...record }));
  }

  /**
   * 设置当前选中版本。
   *
   * @param versionId 版本 ID。
   */
  function selectVersion(versionId: string): void {
    selectedVersionId.value = versionId;
  }

  /**
   * 标记当前正在执行模型切换的版本。
   *
   * @param versionId 正在下载、OpenProject 并落地到 Viewer 的版本 ID。
   */
  function beginSwitchingVersion(versionId: string): void {
    switchingVersionId.value = versionId;
    isVersionSwitching.value = true;
  }

  /**
   * 结束当前版本切换状态。
   *
   * @param versionId 发起结束的版本 ID；传入后只清理仍归属于该版本的 loading。
   */
  function finishSwitchingVersion(versionId?: string): void {
    if (!versionId || switchingVersionId.value === versionId) {
      switchingVersionId.value = null;
      isVersionSwitching.value = false;
    }
  }

  /**
   * 重置版本 Preview 状态。
   */
  function reset(): void {
    records.value = [];
    selectedVersionId.value = null;
    switchingVersionId.value = null;
    isRecordsLoading.value = false;
    isVersionSwitching.value = false;
    recordsError.value = null;
    switchError.value = null;
  }

  return {
    records,
    selectedVersionId,
    switchingVersionId,
    isRecordsLoading,
    isVersionSwitching,
    recordsError,
    switchError,
    selectedRecord,
    adoptedRecord,
    setRecords,
    selectVersion,
    beginSwitchingVersion,
    finishSwitchingVersion,
    reset,
  };
});
